// ─────────────────────────────────────────────────────────────────────────────
// Sync Debug Modal - Hidden Debug Interface for Technical Users
// Provides comprehensive view of sync system internals and IndexedDB data
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { productionLogger } from '../../services/productionLoggingService';
import type { LogEntry } from '../../services/productionLoggingService';
import './SyncDebugModal.css';

interface SyncDebugModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface DebugData {
  syncStats: any;
  outboxStats: any;
  outboxRecords: any[];
  failedRecords: any[];
  backupStats: any;
  health: any;
  databases: any;
}

const SyncDebugModal: React.FC<SyncDebugModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [debugData, setDebugData] = useState<DebugData | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'outbox' | 'backups' | 'indexeddb' | 'tools' | 'logs'>('overview');
  const [operationResult, setOperationResult] = useState<string>('');
  const [selectedStore, setSelectedStore] = useState<string | null>(null);
  const [storeData, setStoreData] = useState<any[]>([]);
  const [loadingStore, setLoadingStore] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [showRecordData, setShowRecordData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [debugLogs, setDebugLogs] = useState<string[]>([]);
  const [productionLogs, setProductionLogs] = useState<LogEntry[]>([]);

  const addDebugLog = (message: string) => {
    const timestamp = new Date().toISOString();
    setDebugLogs(prev => [...prev.slice(-49), `[${timestamp}] ${message}`]);
  };

  const loadDebugData = useCallback(async () => {
    setLoading(true);
    setError(null);
    addDebugLog('Starting debug data load...');
    
    try {
      // Check if we're in a browser environment
      if (typeof window === 'undefined' || typeof indexedDB === 'undefined') {
        throw new Error('Debug modal requires browser environment with IndexedDB support');
      }
      
      // Check basic environment
      addDebugLog(`Navigator online: ${navigator.onLine}`);
      addDebugLog(`Environment: ${process.env.NODE_ENV}`);
      addDebugLog(`API URL: ${process.env.REACT_APP_API_URL ? 'Set' : 'Missing'}`);
      
      // Try to import sync functions safely
      let syncModules;
      try {
        syncModules = await import('../../sync/syncService');
        addDebugLog('Sync service imported successfully');
      } catch (importError) {
        addDebugLog(`Failed to import sync service: ${importError}`);
        // Continue without sync service, set to null to handle gracefully
        syncModules = null;
      }

      // Try to import outbox functions
      let outboxModules;
      try {
        outboxModules = await import('../../sync/outbox');
        addDebugLog('Outbox module imported successfully');
      } catch (importError) {
        addDebugLog(`Failed to import outbox: ${importError}`);
        // Continue without outbox, set to null to handle gracefully
        outboxModules = null;
      }

      // Try to import backup functions
      let backupModules;
      try {
        backupModules = await import('../../hooks/repos/backupMetadataRepo');
        addDebugLog('Backup metadata repo imported successfully');
      } catch (importError) {
        addDebugLog(`Failed to import backup metadata repo: ${importError}`);
        // Backup is optional, continue without it
        backupModules = null;
      }

      const results = [];
      
      // Try each operation individually with error handling
      try {
        const detailedStats = syncModules ? await syncModules.getDetailedSyncStats() : {
          error: 'Sync service not available',
          lastSync: null,
          totalProcessed: 0,
          successCount: 0,
          errorCount: 0,
          isRunning: false
        };
        results.push({ key: 'syncStats', value: detailedStats });
        addDebugLog('Sync stats loaded successfully');
      } catch (err) {
        addDebugLog(`Failed to get sync stats: ${err}`);
        results.push({ key: 'syncStats', value: { error: `Failed to load: ${err instanceof Error ? err.message : 'Unknown error'}` } });
      }

      try {
        const health = syncModules ? await syncModules.checkSyncHealth() : {
          error: 'Sync service not available',
          status: 'unknown',
          issues: ['Sync service could not be loaded']
        };
        results.push({ key: 'health', value: health });
        addDebugLog('Health check completed successfully');
      } catch (err) {
        addDebugLog(`Failed to check health: ${err}`);
        results.push({ key: 'health', value: { error: `Failed to load: ${err instanceof Error ? err.message : 'Unknown error'}` } });
      }

      try {
        const outboxRecords = outboxModules ? await outboxModules.peekOutbox(100) : [];
        results.push({ key: 'outboxRecords', value: outboxRecords });
        addDebugLog(`Outbox records loaded: ${outboxRecords.length} records`);
      } catch (err) {
        addDebugLog(`Failed to peek outbox: ${err}`);
        results.push({ key: 'outboxRecords', value: [] });
      }

      try {
        const outboxStats = outboxModules ? await outboxModules.getOutboxStats() : { 
          total: 0, 
          pending: 0, 
          failed: 0, 
          error: 'Outbox module not available' 
        };
        results.push({ key: 'outboxStats', value: outboxStats });
        addDebugLog('Outbox stats loaded successfully');
      } catch (err) {
        addDebugLog(`Failed to get outbox stats: ${err}`);
        results.push({ key: 'outboxStats', value: { error: `Failed to load: ${err instanceof Error ? err.message : 'Unknown error'}` } });
      }

      try {
        const failedRecords = outboxModules ? await outboxModules.getFailedRecords() : [];
        results.push({ key: 'failedRecords', value: failedRecords });
        addDebugLog(`Failed records loaded: ${failedRecords.length} records`);
      } catch (err) {
        addDebugLog(`Failed to get failed records: ${err}`);
        results.push({ key: 'failedRecords', value: [] });
      }

      try {
        const backupStats = backupModules ? await backupModules.getBackupStatistics() : null;
        results.push({ key: 'backupStats', value: backupStats });
        addDebugLog('Backup stats loaded successfully');
      } catch (err) {
        addDebugLog(`Failed to get backup stats: ${err}`);
        results.push({ key: 'backupStats', value: null });
      }

      try {
        const databases = await getIndexedDBInfo();
        results.push({ key: 'databases', value: databases });
        addDebugLog('IndexedDB info loaded successfully');
      } catch (err) {
        addDebugLog(`Failed to get IndexedDB info: ${err}`);
        results.push({ key: 'databases', value: { error: `Failed to load: ${err instanceof Error ? err.message : 'Unknown error'}` } });
      }

      // Combine results
      const debugData: DebugData = {
        syncStats: results.find(r => r.key === 'syncStats')?.value || {},
        health: results.find(r => r.key === 'health')?.value || {},
        outboxRecords: results.find(r => r.key === 'outboxRecords')?.value || [],
        outboxStats: results.find(r => r.key === 'outboxStats')?.value || {},
        failedRecords: results.find(r => r.key === 'failedRecords')?.value || [],
        backupStats: results.find(r => r.key === 'backupStats')?.value || null,
        databases: results.find(r => r.key === 'databases')?.value || {}
      };

      setDebugData(debugData);
      addDebugLog('Debug data loaded successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(errorMessage);
      addDebugLog(`Critical error: ${errorMessage}`);
      console.error('Failed to load debug data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const getIndexedDBInfo = async (): Promise<any> => {
    try {
      // Get database info
      const dbInfo = {
        allDatabases: [] as any[],
        totalSize: 0
      };

      // List all databases (browser support varies)
      try {
        if ('databases' in indexedDB && typeof (indexedDB as any).databases === 'function') {
          const databases = await (indexedDB as any).databases();
          dbInfo.allDatabases = databases;
        }
      } catch (error) {
        console.warn('Could not enumerate all databases:', error);
      }

      // Get our specific database info
      try {
        const { getDb } = await import('../../hooks/repos/dbrepo');
        const db = await getDb();
        
        const stores = Array.from(db.objectStoreNames);
        const storeInfo = [];

        for (const storeName of stores) {
          try {
            const tx = db.transaction(storeName, 'readonly');
            const store = tx.objectStore(storeName);
            const count = await store.count();
            
            storeInfo.push({
              name: storeName,
              recordCount: count,
              keyPath: store.keyPath,
              autoIncrement: store.autoIncrement,
              indexNames: Array.from(store.indexNames)
            });
          } catch (error) {
            storeInfo.push({
              name: storeName,
              recordCount: 'Error',
              error: error instanceof Error ? error.message : 'Unknown error'
            });
          }
        }

        return {
          ...dbInfo,
          sedationAppDB: {
            name: db.name,
            version: db.version,
            objectStoreNames: Array.from(db.objectStoreNames),
            stores: storeInfo
          }
        };
      } catch (error) {
        return {
          ...dbInfo,
          sedationAppDBError: error instanceof Error ? error.message : 'Unknown error'
        };
      }
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  };

  const loadObjectStoreData = async (storeName: string) => {
    setLoadingStore(true);
    addDebugLog(`Loading data from store: ${storeName}`);
    try {
      const { getDb } = await import('../../hooks/repos/dbrepo');
      const db = await getDb();
      
      // Check if store exists
      if (!db.objectStoreNames.contains(storeName)) {
        addDebugLog(`Store ${storeName} does not exist in database`);
        setStoreData([]);
        setSelectedStore(storeName);
        return;
      }
      
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      
      // Get all records from the store
      const allRecords = await store.getAll();
      
      // Limit to first 100 records for performance
      const limitedRecords = allRecords.slice(0, 100);
      
      setStoreData(limitedRecords);
      setSelectedStore(storeName);
      addDebugLog(`Successfully loaded ${limitedRecords.length} records from ${storeName}`);
    } catch (error) {
      const errorMessage = `Failed to load ${storeName} data: ${error instanceof Error ? error.message : 'Unknown error'}`;
      console.error(errorMessage, error);
      addDebugLog(errorMessage);
      setStoreData([]);
      setSelectedStore(storeName);
    } finally {
      setLoadingStore(false);
    }
  };

  const handleViewRecord = (record: any) => {
    setSelectedRecord(record);
    setShowRecordData(true);
  };

  const handleCopyRecordData = async () => {
    if (selectedRecord) {
      try {
        const jsonString = JSON.stringify(selectedRecord, null, 2);
        await navigator.clipboard.writeText(jsonString);
        alert('Record data copied to clipboard!');
      } catch (error) {
        // Fallback for browsers that don't support clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = JSON.stringify(selectedRecord, null, 2);
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        alert('Record data copied to clipboard!');
      }
    }
  };

  const handleCloseRecordView = () => {
    setSelectedRecord(null);
    setShowRecordData(false);
  };

  const handleOperation = async (operation: string, ...args: any[]) => {
    setLoading(true);
    setOperationResult('');
    addDebugLog(`Starting operation: ${operation}`);
    
    try {
      let result;
      
      switch (operation) {
        case 'testAuth':
          try {
            const authStateJson = localStorage.getItem('UserAuthState');
            const authState = authStateJson ? JSON.parse(authStateJson) : null;
            const isAuthenticated = !!authState?.token?.token;
            const isTokenExpired = authState?.token?.expires && new Date() > new Date(authState.token.expires);
            
            result = {
              isAuthenticated,
              hasAuthState: !!authState,
              hasToken: !!authState?.token,
              tokenExpiry: authState?.token?.expires,
              isTokenExpired,
              userId: authState?.user?.id || authState?.userId,
              currentTime: new Date().toISOString()
            };
            
            setOperationResult(`Authentication Test:\n${JSON.stringify(result, null, 2)}`);
            addDebugLog(`Authentication test completed: ${isAuthenticated ? 'PASSED' : 'FAILED'}`);
          } catch (err) {
            addDebugLog(`Authentication test failed: ${err}`);
            throw err;
          }
          break;

        case 'testNetwork':
          try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const healthEndpoint = `${apiUrl}/api/health`;
            
            result = {
              navigatorOnline: navigator.onLine,
              apiUrl: apiUrl || 'NOT_SET',
              environment: process.env.NODE_ENV,
              userAgent: navigator.userAgent.substring(0, 100) + '...'
            };

            // Try to hit a simple endpoint to test connectivity
            try {
              const response = await fetch(healthEndpoint, { 
                method: 'GET',
                headers: { 'Accept': 'application/json' }
              });
              result.networkTest = {
                endpoint: healthEndpoint,
                status: response.status,
                statusText: response.statusText,
                success: response.ok
              };
            } catch (fetchError) {
              result.networkTest = {
                endpoint: healthEndpoint,
                error: fetchError instanceof Error ? fetchError.message : 'Unknown error',
                success: false
              };
            }
            
            setOperationResult(`Network Test:\n${JSON.stringify(result, null, 2)}`);
            addDebugLog(`Network test completed: ${result.networkTest?.success ? 'PASSED' : 'FAILED'}`);
          } catch (err) {
            addDebugLog(`Network test failed: ${err}`);
            throw err;
          }
          break;

        case 'testLogger':
          try {
            // Test the production logger
            await productionLogger.flushNow();
            result = {
              bufferSize: productionLogger.getRecentLogs().length,
              lastFlush: new Date().toISOString(),
              testLogSent: true
            };
            
            // Send a test log
            productionLogger.info('Debug modal test log', {
              testId: Date.now(),
              source: 'debug-modal-manual-test'
            });
            
            setOperationResult(`Logger Test:\n${JSON.stringify(result, null, 2)}`);
            addDebugLog('Logger test completed - check DataDog for test log');
          } catch (err) {
            addDebugLog(`Logger test failed: ${err}`);
            throw err;
          }
          break;

        case 'forceSync':
          try {
            const syncModule = await import('../../sync/syncService');
            result = await syncModule.forceSyncNow();
            setOperationResult(`Force sync completed: ${JSON.stringify(result, null, 2)}`);
            addDebugLog('Force sync completed successfully');
          } catch (err) {
            addDebugLog(`Force sync failed: ${err}`);
            throw err;
          }
          break;
          
        case 'clearOutbox':
          try {
            const outboxModule = await import('../../sync/outbox');
            result = await outboxModule.clearOutbox();
            setOperationResult(`Cleared ${result} outbox records`);
            addDebugLog(`Cleared ${result} outbox records`);
          } catch (err) {
            addDebugLog(`Clear outbox failed: ${err}`);
            throw err;
          }
          break;
          
        case 'retryFailed':
          if (args[0]) {
            try {
              const outboxModule = await import('../../sync/outbox');
              await outboxModule.retryFailedRecord(args[0]);
              setOperationResult(`Retried failed record: ${args[0]}`);
              addDebugLog(`Retried failed record: ${args[0]}`);
            } catch (err) {
              addDebugLog(`Retry failed record failed: ${err}`);
              throw err;
            }
          }
          break;
          
        case 'resetStats':
          try {
            const syncModule = await import('../../sync/syncService');
            syncModule.resetSyncStats();
            setOperationResult('Sync statistics reset');
            addDebugLog('Sync statistics reset successfully');
          } catch (err) {
            addDebugLog(`Reset stats failed: ${err}`);
            throw err;
          }
          break;
          
        case 'exportData':
          try {
            const exportData = {
              timestamp: new Date().toISOString(),
              syncStats: debugData?.syncStats,
              outboxStats: debugData?.outboxStats,
              outboxRecords: debugData?.outboxRecords,
              failedRecords: debugData?.failedRecords,
              backupStats: debugData?.backupStats,
              health: debugData?.health,
              databases: debugData?.databases,
              debugLogs: debugLogs
            };
            
            const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `sync-debug-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            
            setOperationResult('Debug data exported successfully');
            addDebugLog('Debug data exported successfully');
          } catch (err) {
            addDebugLog(`Export data failed: ${err}`);
            throw err;
          }
          break;
          
        default:
          const errorMsg = `Unknown operation: ${operation}`;
          setOperationResult(errorMsg);
          addDebugLog(errorMsg);
          return;
      }
      
      // Reload data after operation
      setTimeout(() => {
        addDebugLog('Reloading debug data after operation...');
        loadDebugData();
      }, 1000);
    } catch (error) {
      const errorMessage = `Operation failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
      setOperationResult(errorMessage);
      addDebugLog(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadDebugData();
      
      // Load existing production logs
      setProductionLogs(productionLogger.getRecentLogs());
      
      // Listen for real-time production logs
      const handleProductionLog = (event: CustomEvent<LogEntry>) => {
        setProductionLogs(prev => [event.detail, ...prev.slice(0, 99)]); // Keep last 100 logs
      };
      
      window.addEventListener('production-log', handleProductionLog as EventListener);
      
      // Auto-refresh every 10 seconds while open
      const interval = setInterval(loadDebugData, 10000);
      
      return () => {
        clearInterval(interval);
        window.removeEventListener('production-log', handleProductionLog as EventListener);
      };
    }
  }, [isOpen, loadDebugData]);

  if (!isOpen) return null;

  return (
    <div className="sync-debug-modal-overlay" onClick={onClose}>
      <div className="sync-debug-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sync-debug-header">
          <h2>🔧 Sync Debug Console</h2>
          <div className="sync-debug-actions">
            <button 
              className="btn-secondary btn-sm" 
              onClick={loadDebugData}
              disabled={loading}
            >
              {loading ? '🔄' : '🔄'} Refresh
            </button>
            <button className="btn-close" onClick={onClose}>×</button>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="debug-error-banner" style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            padding: '12px',
            margin: '16px',
            color: '#dc2626'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>⚠️</span>
              <strong>Debug System Error:</strong>
            </div>
            <div style={{ marginTop: '8px', fontSize: '14px' }}>
              {error}
            </div>
            <button 
              onClick={() => setError(null)}
              style={{
                marginTop: '8px',
                padding: '4px 8px',
                background: 'transparent',
                border: '1px solid #dc2626',
                borderRadius: '4px',
                color: '#dc2626',
                cursor: 'pointer'
              }}
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="sync-debug-tabs">
          {(['overview', 'outbox', 'backups', 'indexeddb', 'tools', 'logs'] as const).map(tab => (
            <button
              key={tab}
              className={`debug-tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        <div className="sync-debug-content">
          {error && (
            <div className="debug-error">
              <h3>❌ Error Loading Debug Data</h3>
              <p>{error}</p>
              <p>Check the logs tab for more details.</p>
              <button 
                className="btn-primary btn-sm" 
                onClick={() => {
                  setError(null);
                  loadDebugData();
                }}
              >
                Retry
              </button>
            </div>
          )}

          {loading && (
            <div className="debug-loading">
              <div className="loading-spinner"></div>
              <p>Loading debug data...</p>
              {debugLogs.length > 0 && (
                <div className="debug-loading-logs">
                  <p>Loading progress:</p>
                  <div className="debug-log-messages">
                    {debugLogs.slice(-5).map((log, idx) => (
                      <p key={idx} className="debug-log-item">{log}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!loading && !debugData && !error && (
            <div className="debug-section">
              <div className="debug-empty">
                <h3>🔄 No Data Loaded</h3>
                <p>Click the Refresh button to load debug data, or check the Logs tab for loading details.</p>
                <button 
                  className="btn-primary"
                  onClick={loadDebugData}
                >
                  Load Debug Data
                </button>
              </div>
            </div>
          )}

          {!loading && debugData && (
            <>
              {activeTab === 'overview' && (
                <div className="debug-section">
                  <h3>📊 Sync Status Overview</h3>
                  
                  <div className="debug-cards">
                    <div className="debug-card">
                      <h4>Sync Statistics</h4>
                      {debugData.syncStats?.error ? (
                        <div className="debug-error-inline">
                          <p>⚠️ {debugData.syncStats.error}</p>
                        </div>
                      ) : (
                        <>
                          <div className="debug-item">
                            <span className="debug-label">Status:</span>
                            <span className={`debug-value ${debugData.syncStats?.isRunning ? 'status-running' : 'status-stopped'}`}>
                              {debugData.syncStats?.isRunning ? '🟢 Running' : '🔴 Stopped'}
                            </span>
                          </div>
                          <div className="debug-item">
                            <span className="debug-label">Last Sync:</span>
                            <span className="debug-value">{debugData.syncStats?.lastSync || 'Never'}</span>
                          </div>
                          <div className="debug-item">
                            <span className="debug-label">Total Processed:</span>
                            <span className="debug-value">{debugData.syncStats?.totalProcessed || 0}</span>
                          </div>
                          <div className="debug-item">
                            <span className="debug-label">Success/Error:</span>
                            <span className="debug-value">
                              {debugData.syncStats?.successCount || 0} / {debugData.syncStats?.errorCount || 0}
                            </span>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="debug-card">
                      <h4>Outbox Status</h4>
                      {debugData.outboxStats?.error ? (
                        <div className="debug-error-inline">
                          <p>⚠️ {debugData.outboxStats.error}</p>
                        </div>
                      ) : (
                        <>
                          <div className="debug-item">
                            <span className="debug-label">Total Records:</span>
                            <span className="debug-value">{debugData.outboxStats?.total || 0}</span>
                          </div>
                          <div className="debug-item">
                            <span className="debug-label">Pending:</span>
                            <span className="debug-value">{debugData.outboxStats?.pending || 0}</span>
                          </div>
                          <div className="debug-item">
                            <span className="debug-label">Failed:</span>
                            <span className="debug-value status-error">{debugData.outboxStats?.failed || 0}</span>
                          </div>
                          {debugData.outboxStats?.oldestRecord && (
                            <div className="debug-item">
                              <span className="debug-label">Oldest Record:</span>
                              <span className="debug-value">
                                {new Date(debugData.outboxStats.oldestRecord.enqueuedAt).toLocaleString()}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    <div className="debug-card">
                      <h4>Health Check</h4>
                      {debugData.health.error ? (
                        <div className="debug-error-inline">
                          <p>⚠️ {debugData.health.error}</p>
                        </div>
                      ) : (
                        <>
                          <div className="debug-item">
                            <span className="debug-label">Overall Health:</span>
                            <span className={`debug-value ${debugData.health.isHealthy ? 'status-healthy' : 'status-error'}`}>
                              {debugData.health.isHealthy ? '✅ Healthy' : '❌ Issues Detected'}
                            </span>
                          </div>
                          {debugData.health.issues && debugData.health.issues.length > 0 && (
                            <div className="debug-issues">
                              <p><strong>Issues:</strong></p>
                              <ul>
                                {debugData.health.issues.map((issue: string, idx: number) => (
                                  <li key={idx} className="debug-issue">{issue}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {debugData.health.recommendations && debugData.health.recommendations.length > 0 && (
                            <div className="debug-recommendations">
                              <p><strong>Recommendations:</strong></p>
                              <ul>
                                {debugData.health.recommendations.map((rec: string, idx: number) => (
                                  <li key={idx} className="debug-recommendation">{rec}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'outbox' && (
                <div className="debug-section">
                  <h3>📤 Outbox Records ({debugData.outboxRecords.length})</h3>
                  
                  {debugData.outboxRecords.length === 0 ? (
                    <p className="debug-empty">No outbox records found</p>
                  ) : (
                    <div className="debug-table-container">
                      <table className="debug-table">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Entity</th>
                            <th>Operation</th>
                            <th>Enqueued At</th>
                            <th>Attempts</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {debugData.outboxRecords.map((record: any) => (
                            <tr key={record.id} className={record.attempts >= 3 ? 'record-failed' : ''}>
                              <td className="record-id" title={record.id}>{record.id.substring(0, 8)}...</td>
                              <td>{record.entityType}</td>
                              <td>{record.operation}</td>
                              <td>{new Date(record.enqueuedAt).toLocaleString()}</td>
                              <td className={record.attempts >= 3 ? 'attempts-failed' : ''}>{record.attempts}</td>
                              <td>
                                {record.attempts >= 3 && (
                                  <button
                                    className="btn-xs btn-primary"
                                    onClick={() => handleOperation('retryFailed', record.id)}
                                    disabled={loading}
                                  >
                                    Retry
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {debugData.failedRecords.length > 0 && (
                    <div className="debug-failed-records">
                      <h4>⚠️ Failed Records ({debugData.failedRecords.length})</h4>
                      <div className="debug-table-container">
                        <table className="debug-table">
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>Entity</th>
                              <th>Last Attempt</th>
                              <th>Attempts</th>
                              <th>Data Preview</th>
                            </tr>
                          </thead>
                          <tbody>
                            {debugData.failedRecords.map((record: any) => (
                              <tr key={record.id}>
                                <td className="record-id" title={record.id}>{record.id.substring(0, 8)}...</td>
                                <td>{record.entityType}</td>
                                <td>{new Date(record.enqueuedAt).toLocaleString()}</td>
                                <td>{record.attempts}</td>
                                <td className="data-preview">
                                  {record.data?.eventType || record.data?.SedationDetail?.Id || 'No preview'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'backups' && (
                <div className="debug-section">
                  <h3>💾 Backup Statistics</h3>
                  
                  {debugData.backupStats ? (
                    <div className="debug-cards">
                      <div className="debug-card">
                        <h4>Backup Counts</h4>
                        <div className="debug-item">
                          <span className="debug-label">Total Backups:</span>
                          <span className="debug-value">{debugData.backupStats.totalBackups}</span>
                        </div>
                        <div className="debug-item">
                          <span className="debug-label">Pending Sync:</span>
                          <span className="debug-value status-warning">{debugData.backupStats.pendingBackups}</span>
                        </div>
                        <div className="debug-item">
                          <span className="debug-label">With PDFs:</span>
                          <span className="debug-value">{debugData.backupStats.backupsWithPdfs}</span>
                        </div>
                        <div className="debug-item">
                          <span className="debug-label">Total Images:</span>
                          <span className="debug-value">{debugData.backupStats.totalImagesCount}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="debug-empty">Backup statistics not available</p>
                  )}
                </div>
              )}

              {activeTab === 'indexeddb' && (
                <div className="debug-section">
                  <h3>🗄️ IndexedDB Information</h3>
                  
                  {selectedStore && (
                    <div className="debug-card" style={{ marginBottom: '20px' }}>
                      <h4>📋 {selectedStore} Data ({storeData.length} records)</h4>
                      <button
                        className="btn-secondary btn-sm"
                        onClick={() => {
                          setSelectedStore(null);
                          setStoreData([]);
                        }}
                        style={{ marginBottom: '16px' }}
                      >
                        ← Back to Stores
                      </button>
                      
                      {loadingStore ? (
                        <div className="debug-loading">
                          <div className="loading-spinner"></div>
                          <p>Loading {selectedStore} data...</p>
                        </div>
                      ) : storeData.length > 0 ? (
                        <div className="debug-table-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                          <table className="debug-table">
                            <thead>
                              <tr>
                                <th>Key/ID</th>
                                <th>Data Preview</th>
                                <th>Type</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {storeData.map((record: any, idx: number) => {
                                const key = record.id || record.Id || record._id || record.key || `Record ${idx}`;
                                const preview = typeof record === 'object' 
                                  ? JSON.stringify(record).substring(0, 100) + (JSON.stringify(record).length > 100 ? '...' : '')
                                  : String(record).substring(0, 100);
                                
                                return (
                                  <tr key={idx}>
                                    <td className="record-id" title={String(key)}>{String(key).substring(0, 20)}...</td>
                                    <td className="data-preview" title={JSON.stringify(record, null, 2)}>{preview}</td>
                                    <td>{typeof record}</td>
                                    <td>
                                      <button
                                        className="btn-xs btn-secondary"
                                        onClick={() => handleViewRecord(record)}
                                      >
                                        View Data
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="debug-empty">No data found in {selectedStore}</p>
                      )}
                    </div>
                  )}

                  {!selectedStore && (
                    <>
                      {debugData.databases.error ? (
                        <p className="debug-error">Error loading IndexedDB info: {debugData.databases.error}</p>
                      ) : (
                        <>
                          {debugData.databases.sedationAppDB && (
                            <div className="debug-card">
                              <h4>Sedation App Database</h4>
                              <div className="debug-item">
                                <span className="debug-label">Name:</span>
                                <span className="debug-value">{debugData.databases.sedationAppDB.name}</span>
                              </div>
                              <div className="debug-item">
                                <span className="debug-label">Version:</span>
                                <span className="debug-value">{debugData.databases.sedationAppDB.version}</span>
                              </div>
                              
                              <h5>Object Stores (Click to view data):</h5>
                              <div className="debug-table-container">
                                <table className="debug-table">
                                  <thead>
                                    <tr>
                                      <th>Store Name</th>
                                      <th>Record Count</th>
                                      <th>Key Path</th>
                                      <th>Auto Increment</th>
                                      <th>Indexes</th>
                                      <th>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {debugData.databases.sedationAppDB.stores.map((store: any) => (
                                      <tr key={store.name} style={{ cursor: store.error ? 'default' : 'pointer' }}>
                                        <td>
                                          <strong 
                                            style={{ 
                                              color: store.error ? '#dc2626' : 'var(--brand-navy-dark)', 
                                              textDecoration: store.error ? 'none' : 'underline' 
                                            }}
                                            onClick={() => !store.error && loadObjectStoreData(store.name)}
                                          >
                                            {store.name}
                                          </strong>
                                        </td>
                                        <td>{store.error ? 'Error' : store.recordCount}</td>
                                        <td>{store.keyPath || 'None'}</td>
                                        <td>{store.autoIncrement ? 'Yes' : 'No'}</td>
                                        <td>{store.indexNames?.length || 0} indexes</td>
                                        <td>
                                          {!store.error && (
                                            <button
                                              className="btn-xs btn-primary"
                                              onClick={() => loadObjectStoreData(store.name)}
                                              disabled={loadingStore}
                                            >
                                              View Data
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {debugData.databases.allDatabases?.length > 0 && (
                            <div className="debug-card">
                              <h4>All IndexedDB Databases in Browser</h4>
                              <ul className="debug-database-list">
                                {debugData.databases.allDatabases.map((db: any, idx: number) => (
                                  <li key={idx}>
                                    <strong>{db.name || 'Unnamed Database'}</strong> 
                                    {db.version && ` (v${db.version})`}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {(!debugData.databases.allDatabases || debugData.databases.allDatabases.length === 0) && (
                            <div className="debug-card">
                              <h4>All IndexedDB Databases</h4>
                              <p className="debug-empty">
                                Browser doesn't support database enumeration or no databases found.
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {activeTab === 'tools' && (
                <div className="debug-section">
                  <h3>🛠️ Debug Tools</h3>
                  
                  <div className="debug-tools">
                    <div className="tool-group">
                      <h4>🔍 Diagnostics</h4>
                      <div className="tool-buttons">
                        <button
                          className="btn-primary"
                          onClick={() => handleOperation('testAuth')}
                          disabled={loading}
                        >
                          🔐 Test Authentication
                        </button>
                        <button
                          className="btn-primary"
                          onClick={() => handleOperation('testNetwork')}
                          disabled={loading}
                        >
                          🌐 Test Network & API
                        </button>
                        <button
                          className="btn-primary"
                          onClick={() => handleOperation('testLogger')}
                          disabled={loading}
                        >
                          📤 Test Remote Logging
                        </button>
                      </div>
                    </div>

                    <div className="tool-group">
                      <h4>🚀 Sync Operations</h4>
                      <div className="tool-buttons">
                        <button
                          className="btn-primary"
                          onClick={() => handleOperation('forceSync')}
                          disabled={loading}
                        >
                          � Force Sync Now
                        </button>
                        <button
                          className="btn-secondary"
                          onClick={() => handleOperation('resetStats')}
                          disabled={loading}
                        >
                          📊 Reset Statistics
                        </button>
                        <button
                          className="btn-secondary"
                          onClick={() => handleOperation('clearOutbox')}
                          disabled={loading}
                        >
                          🗑️ Clear Outbox
                        </button>
                      </div>
                    </div>

                    <div className="tool-group">
                      <h4>Outbox Management</h4>
                      <div className="tool-buttons">
                        <button
                          className="btn-danger"
                          onClick={() => {
                            if (window.confirm('Are you sure you want to clear all outbox records? This cannot be undone.')) {
                              handleOperation('clearOutbox');
                            }
                          }}
                          disabled={loading}
                        >
                          🗑️ Clear Outbox
                        </button>
                      </div>
                    </div>

                    <div className="tool-group">
                      <h4>Data Export</h4>
                      <div className="tool-buttons">
                        <button
                          className="btn-secondary"
                          onClick={() => handleOperation('exportData')}
                          disabled={loading}
                        >
                          📁 Export Debug Data
                        </button>
                      </div>
                    </div>

                    <div className="tool-group">
                      <h4>📥 Report Recovery</h4>
                      <p style={{ fontSize: '0.8rem', color: 'var(--text-muted, #888)', marginBottom: '8px' }}>
                        Import a backup JSON payload to inspect sedation record data when a report fails to generate.
                      </p>
                      <div className="tool-buttons">
                        <button
                          className="btn-primary"
                          onClick={() => { onClose(); navigate('/import-report'); }}
                        >
                          📥 Open Import Tool
                        </button>
                      </div>
                    </div>
                  </div>

                  {operationResult && (
                    <div className="operation-result">
                      <h4>Operation Result:</h4>
                      <pre className="result-text">{operationResult}</pre>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'logs' && (
                <div className="debug-section">
                  <h3>📋 System Logs</h3>
                  
                  {/* Production Logs Section */}
                  <div className="debug-card" style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <h4>🔴 Production Logs (Real-time)</h4>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button 
                          className="btn-secondary btn-sm"
                          onClick={() => productionLogger.flushNow()}
                        >
                          📤 Flush to Remote
                        </button>
                        <button 
                          className="btn-secondary btn-sm"
                          onClick={() => setProductionLogs([])}
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    
                    <div className="debug-item" style={{ marginBottom: '12px' }}>
                      <span className="debug-label">Live Logs:</span>
                      <span className="debug-value">{productionLogs.length}</span>
                      <span className="debug-label" style={{ marginLeft: '20px' }}>Errors:</span>
                      <span className="debug-value status-error">
                        {productionLogs.filter(log => log.Level === 'error' || log.Level === 'critical').length}
                      </span>
                    </div>
                    
                    <div className="debug-logs-content" style={{ maxHeight: '300px', overflow: 'auto' }}>
                      {productionLogs.length === 0 ? (
                        <p className="debug-empty">No production logs yet. Logs will appear here in real-time as sync operations occur.</p>
                      ) : (
                        productionLogs.map((log, idx) => (
                          <div key={idx} className={`debug-log-entry production-log-${log.Level}`}>
                            <div className="log-header">
                              <span className={`log-level log-level-${log.Level}`}>
                                {log.Level?.toUpperCase()}
                              </span>
                              <span className="log-category">[{log.Properties?.SourceContext || 'general'}]</span>
                              <span className="log-timestamp">{new Date(log.Timestamp).toLocaleTimeString()}</span>
                            </div>
                            <div className="log-message">{log.RenderedMessage || log.MessageTemplate}</div>
                            {log.Properties && Object.keys(log.Properties).length > 0 && (
                              <details className="log-metadata">
                                <summary>Show Details</summary>
                                <pre>{JSON.stringify(log.Properties, null, 2)}</pre>
                              </details>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Debug Logs Section */}
                  <div className="debug-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <h4>🔧 Debug Logs (Internal)</h4>
                      <button 
                        className="btn-secondary btn-sm"
                        onClick={() => setDebugLogs([])}
                      >
                        Clear Logs
                      </button>
                    </div>
                    
                    <div className="debug-item" style={{ marginBottom: '12px' }}>
                      <span className="debug-label">Debug entries:</span>
                      <span className="debug-value">{debugLogs.length}</span>
                    </div>
                    
                    <div className="debug-logs-content" style={{ maxHeight: '200px', overflow: 'auto' }}>
                      {debugLogs.length === 0 ? (
                        <p className="debug-empty">No debug logs available. Refresh the debug data to see loading logs.</p>
                      ) : (
                        debugLogs.map((log, idx) => (
                          <div key={idx} className="debug-log-entry debug-log-internal">
                            <code>{log}</code>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Record Data Viewer Modal */}
      {showRecordData && selectedRecord && (
        <div className="sync-debug-modal-overlay" onClick={handleCloseRecordView}>
          <div className="sync-debug-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="sync-debug-header">
              <h2>📋 Record Data Viewer</h2>
              <div className="sync-debug-actions">
                <button 
                  className="btn-secondary btn-sm" 
                  onClick={handleCopyRecordData}
                >
                  📋 Copy Data
                </button>
                <button className="btn-close" onClick={handleCloseRecordView}>×</button>
              </div>
            </div>
            
            <div className="sync-debug-content">
              <div className="debug-section">
                <h3>Record Details</h3>
                <div className="debug-card">
                  <h4>Store: {selectedStore}</h4>
                  <div className="debug-item">
                    <span className="debug-label">Record Key/ID:</span>
                    <span className="debug-value">
                      {selectedRecord.id || selectedRecord.Id || selectedRecord._id || selectedRecord.key || 'No ID found'}
                    </span>
                  </div>
                  <div className="debug-item">
                    <span className="debug-label">Data Type:</span>
                    <span className="debug-value">{typeof selectedRecord}</span>
                  </div>
                  {selectedRecord.createdAt && (
                    <div className="debug-item">
                      <span className="debug-label">Created At:</span>
                      <span className="debug-value">{new Date(selectedRecord.createdAt).toLocaleString()}</span>
                    </div>
                  )}
                  {selectedRecord.updatedAt && (
                    <div className="debug-item">
                      <span className="debug-label">Updated At:</span>
                      <span className="debug-value">{new Date(selectedRecord.updatedAt).toLocaleString()}</span>
                    </div>
                  )}
                </div>
                
                <div className="debug-card">
                  <h4>Raw JSON Data</h4>
                  <div className="operation-result">
                    <pre className="result-text" style={{ maxHeight: '400px', overflow: 'auto' }}>
                      {JSON.stringify(selectedRecord, null, 2)}
                    </pre>
                  </div>
                  <div style={{ marginTop: '16px', textAlign: 'center' }}>
                    <button 
                      className="btn-primary" 
                      onClick={handleCopyRecordData}
                    >
                      📋 Copy to Clipboard
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SyncDebugModal;
